import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { motion } from 'framer-motion';
import './ContactForm.css';

const ContactForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm();

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));

      console.log('Form submitted:', data);
      setSubmitStatus('success');
      reset();
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const fieldVariants = {
    hidden: { opacity: 0, y: 20, scale: 0.95 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { duration: 0.5, ease: "easeOut" }
    }
  };

  return (
    <div className="contact-form-container">
      <motion.form
        onSubmit={handleSubmit(onSubmit)}
        className="contact-form"
        variants={formVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
      >
        <div className="form-row">
          <motion.div className="form-group" variants={fieldVariants}>
            <label htmlFor="firstName">First Name *</label>
            <motion.input
              type="text"
              id="firstName"
              {...register('firstName', {
                required: 'First name is required',
                minLength: { value: 2, message: 'Minimum 2 characters' }
              })}
              className={errors.firstName ? 'error-input' : ''}
              whileFocus={{ scale: 1.02, borderColor: "var(--secondary-color)" }}
              transition={{ type: "spring", stiffness: 300 }}
            />
            {errors.firstName && (
              <motion.span
                className="error-message"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
              >
                {errors.firstName.message}
              </motion.span>
            )}
          </motion.div>

          <motion.div className="form-group" variants={fieldVariants}>
            <label htmlFor="lastName">Last Name *</label>
            <motion.input
              type="text"
              id="lastName"
              {...register('lastName', {
                required: 'Last name is required',
                minLength: { value: 2, message: 'Minimum 2 characters' }
              })}
              className={errors.lastName ? 'error-input' : ''}
              whileFocus={{ scale: 1.02, borderColor: "var(--secondary-color)" }}
              transition={{ type: "spring", stiffness: 300 }}
            />
            {errors.lastName && (
              <motion.span
                className="error-message"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
              >
                {errors.lastName.message}
              </motion.span>
            )}
          </motion.div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="email">Email Address *</label>
            <input
              type="email"
              id="email"
              {...register('email', { 
                required: 'Email is required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Invalid email address'
                }
              })}
              className={errors.email ? 'error-input' : ''}
            />
            {errors.email && (
              <span className="error-message">{errors.email.message}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="phone">Phone Number *</label>
            <input
              type="tel"
              id="phone"
              {...register('phone', { 
                required: 'Phone number is required',
                pattern: {
                  value: /^[+]?[0-9\s\-\(\)]{10,}$/,
                  message: 'Invalid phone number'
                }
              })}
              className={errors.phone ? 'error-input' : ''}
            />
            {errors.phone && (
              <span className="error-message">{errors.phone.message}</span>
            )}
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="serviceType">Service Interest</label>
          <select
            id="serviceType"
            {...register('serviceType')}
          >
            <option value="">Select Service Type</option>
            <option value="construction">Construction Services</option>
            <option value="property-development">Property Development</option>
            <option value="consultancy">Consultancy & Advisory</option>
            <option value="materials">Construction Materials & Trading</option>
            <option value="real-estate">Real Estate Services</option>
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="projectType">Project Type</label>
          <select
            id="projectType"
            {...register('projectType')}
          >
            <option value="">Select Project Type</option>
            <option value="residential">Residential</option>
            <option value="commercial">Commercial</option>
            <option value="industrial">Industrial</option>
            <option value="mixed-use">Mixed-use</option>
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="message">Message</label>
          <textarea
            id="message"
            rows="4"
            {...register('message')}
            placeholder="Tell us about your construction or real estate requirements..."
          ></textarea>
        </div>

        <motion.button 
          type="submit" 
          className="contact-form-submit-btn"
          disabled={isSubmitting}
          whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
          whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
        >
          {isSubmitting ? 'Sending...' : 'Send Message'}
        </motion.button>

        {submitStatus === 'success' && (
          <div className="status-message success">
            Thank you! Your message has been sent successfully.
          </div>
        )}

        {submitStatus === 'error' && (
          <div className="status-message error">
            Sorry, there was an error sending your message. Please try again.
          </div>
        )}
      </motion.form>
    </div>
  );
};

export default ContactForm;